/**
 * LDS package order rows: rich, stable view-model for dashboard cards and lists.
 * Does not require order_response.reservation; merges row-level, snapshot, and nested hotel payloads.
 */

import { getLdsBoardBasisLabelHebrew } from "../ldsBoardBasisHebrew.js";
import {
  HOTEL_NAME_UNAVAILABLE,
  resolveOrderHotelNameSsot,
} from "./orderHotelNameSsot.js";

function nonEmptyObject(o) {
  return o != null && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;
}

/**
 * order_response לפעמים אובייקט ולפעמים מחרוזת JSON מה-API — מאחד לפני גישה ל-reservation / extended / deal.
 * @param {object} row
 */
export function safeOrderResponseObject(row) {
  if (!row || row.order_response == null) return null;
  const r = row.order_response;
  if (typeof r === "object" && !Array.isArray(r)) return r;
  if (typeof r === "string") {
    const t = r.trim();
    if (t.length < 2) return null;
    try {
      const p = JSON.parse(t);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

function pickTrimmedString(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === "number" && Number.isFinite(c)) {
      const s = String(c).trim();
      if (s) return s;
      continue;
    }
    if (typeof c === "string") {
      const t = c.trim();
      if (t) return t;
    }
  }
  return "";
}

/** @param {unknown} v */
export function normalizePackageCardImageUrl(v) {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t.length ? t : null;
  }
  if (typeof v === "object" && !Array.isArray(v)) {
    return (
      normalizePackageCardImageUrl(/** @type {any} */ (v).url) ||
      normalizePackageCardImageUrl(/** @type {any} */ (v).src) ||
      normalizePackageCardImageUrl(/** @type {any} */ (v).image) ||
      normalizePackageCardImageUrl(/** @type {any} */ (v).thumbnail) ||
      normalizePackageCardImageUrl(/** @type {any} */ (v).link)
    );
  }
  return null;
}

/**
 * Ordered list of image URL candidates from a hotel-like object (reservation / row.hotel / snapshot).
 * @param {object|null|undefined} h
 */
export function collectHotelLikeImageUrls(h) {
  if (!h || typeof h !== "object") return [];
  const out = [];
  const push = (x) => {
    const u = normalizePackageCardImageUrl(x);
    if (u && !out.includes(u)) out.push(u);
  };
  push(/** @type {any} */ (h).resourceUrl);
  push(/** @type {any} */ (h).mainImage);
  push(/** @type {any} */ (h).main_image);
  push(/** @type {any} */ (h).hotelMainImage);
  push(/** @type {any} */ (h).hotelImage);
  push(/** @type {any} */ (h).hotel_image);
  if (typeof /** @type {any} */ (h).image === "string") push(/** @type {any} */ (h).image);
  const images = /** @type {any} */ (h).images;
  if (Array.isArray(images)) {
    for (const item of images) push(item);
  }
  return out;
}

/** @param {object} row */
function rowLevelDirectImageUrls(row) {
  const out = [];
  const push = (x) => {
    const u = normalizePackageCardImageUrl(x);
    if (u && !out.includes(u)) out.push(u);
  };
  push(row.hotelimage);
  push(row.hotelImage);
  push(row.thumbnail);
  push(row.image);
  return out;
}

/** @param {object} row */
function collectDealSnapshotHotels(row) {
  const hotels = [];
  const or = safeOrderResponseObject(row);
  const snaps = [
    row.deal_snapshot,
    row.dealSnapshot,
    or?.deal_snapshot,
    or?.dealSnapshot,
  ];
  for (const s of snaps) {
    const h = s?.hotel;
    if (nonEmptyObject(h)) hotels.push(h);
  }
  const ext = or?.extended ?? row.extended;
  if (Array.isArray(ext)) {
    for (const block of ext) {
      const h = block?.hotel;
      if (nonEmptyObject(h)) hotels.push(h);
    }
  }
  return hotels;
}

/**
 * Production PackageCard (main) loads /getImage using reservation.hotel.hotelId|hotelid|id.
 * LDS rows often omit reservation but keep a hotel blob on extended / deal_snapshot / deal — we already
 * scan those for image URLs; this picks the first numeric Gemonix-style id (same bounds as /getImage).
 * @param {object[]} hotels
 */
function firstNumericHotelIdFromHotelObjects(hotels) {
  if (!Array.isArray(hotels)) return null;
  for (const h of hotels) {
    if (!nonEmptyObject(h)) continue;
    const candidates = [
      h.hotelId,
      h.hotelID,
      h.HotelId,
      h.hotelid,
      h.HOTEL_ID,
      h.id,
    ];
    for (const raw of candidates) {
      if (raw == null || raw === "") continue;
      if (typeof raw === "number" && Number.isFinite(raw) && raw > 0 && raw < 1e12) {
        return Math.floor(raw);
      }
      const s = String(raw).trim();
      if (!/^\d+$/.test(s)) continue;
      const n = parseInt(s, 10);
      if (Number.isFinite(n) && n > 0 && n < 1e12) return n;
    }
  }
  return null;
}

/** @param {object} row */
function pickBoardRaw(row, resHotel, rowHotel) {
  const from = [
    resHotel?.board,
    resHotel?.mealPlan,
    resHotel?.boardBasis,
    resHotel?.BOARD,
    rowHotel?.board,
    rowHotel?.mealPlan,
    row.hotel?.board,
    row.board,
    row.board_basis,
    row.boardBasis,
  ];
  for (const b of from) {
    if (b != null && String(b).trim() !== "") return b;
  }
  return null;
}

/** @param {string} s */
function parseLooseLocalDate(s) {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(t);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = new Date(t);
  return Number.isNaN(iso.getTime()) ? null : iso;
}

/**
 * @param {string} checkIn
 * @param {string} checkOut
 */
function computeNightsBetween(checkIn, checkOut) {
  const d1 = parseLooseLocalDate(checkIn);
  const d2 = parseLooseLocalDate(checkOut);
  if (!d1 || !d2) return null;
  const ms = d2.getTime() - d1.getTime();
  if (ms < 0) return null;
  const n = Math.round(ms / 86400000);
  return n > 0 ? n : null;
}

/** @param {object} row */
function countRoomsAndPax(row) {
  const rooms = row.passengersinfo ?? row.passengers_info ?? row.passengersInfo;
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return { rooms: 0, adults: 0, children: 0 };
  }
  let adults = 0;
  let children = 0;
  for (const r of rooms) {
    if (!r || typeof r !== "object") continue;
    adults += Array.isArray(r.adults) ? r.adults.length : 0;
    const kids = Array.isArray(r.kids) ? r.kids.length : 0;
    const ch = Array.isArray(r.children) ? r.children.length : 0;
    children += kids + ch;
  }
  return { rooms: rooms.length, adults, children };
}

/**
 * @param {object|null|undefined} row
 */
export function getPackageOrderCardSummary(row) {
  const empty = {
    hotelName: "",
    displayTitle: "",
    hotelSubtitle: "",
    checkInDisplay: "",
    checkOutDisplay: "",
    hotelId: null,
    prefetchedImageUrl: null,
    nights: null,
    boardLabel: "",
    roomsCount: 0,
    adultsCount: 0,
    childrenCount: 0,
    passengersLine: "",
    orderIdDisplay: "",
    orderIdRaw: "",
    supplierLine: "",
    kindLabel: "חבילה",
    entityTypeLabel: "",
  };

  if (!row || typeof row !== "object") return empty;

  const or = safeOrderResponseObject(row);
  const reservation = or?.reservation;
  const resHotel = reservation?.hotel;
  const hasResHotel = nonEmptyObject(resHotel);
  const rowHotel = nonEmptyObject(row.hotel) ? row.hotel : null;

  const searchResultHotel =
    row.search_result?.hotel ||
    row.search_result?.deal?.hotel ||
    (Array.isArray(row.search_results) && row.search_results[0]?.hotel) ||
    or?.search_result?.hotel ||
    or?.search_result?.deal?.hotel ||
    (Array.isArray(or?.search_results) && or.search_results[0]?.hotel) ||
    null;
  const hasSrHotel = nonEmptyObject(searchResultHotel);

  /** Hotel name — SSOT (orderHotelNameSsot.js) */
  const ssot = resolveOrderHotelNameSsot(row, { emitTrace: false });
  const hotelName = ssot.resolved ? ssot.hotelName : "";
  const displayTitle = hotelName || HOTEL_NAME_UNAVAILABLE;

  const dealBlock = or?.deal ?? row.deal;
  const pkgHotel = row.package?.hotel;

  /** Destination / city / IATA */
  const destParts = [];
  const pushPart = (p) => {
    const t = pickTrimmedString(p);
    if (t && !destParts.includes(t)) destParts.push(t);
  };

  if (hasResHotel) {
    pushPart(resHotel.hotelCity);
    pushPart(resHotel.destination);
    pushPart(resHotel.destCity);
    pushPart(resHotel.city);
    pushPart(resHotel.country);
    if (typeof resHotel.location === "string" && resHotel.location.length < 80) {
      pushPart(resHotel.location);
    }
  }
  if (rowHotel) {
    pushPart(rowHotel.hotelCity ?? rowHotel.hotelcity);
    pushPart(rowHotel.destination);
    pushPart(rowHotel.city);
    pushPart(rowHotel.country);
  }
  if (hasSrHotel) {
    pushPart(searchResultHotel.hotelCity ?? searchResultHotel.hotelcity);
    pushPart(searchResultHotel.destination);
    pushPart(searchResultHotel.city);
    pushPart(searchResultHotel.country);
  }
  pushPart(row.destination);
  pushPart(row.city);
  pushPart(row.dest);
  pushPart(row.country);
  pushPart(row.iata);
  pushPart(row.destination_code);
  pushPart(row.destinationCode);
  pushPart(row.to_iata);
  pushPart(row.toIata);
  if (!destParts.length && pickTrimmedString(row.hoteladdress)) {
    pushPart(row.hoteladdress);
  }

  const hotelSubtitle = destParts.join(" · ");

  const checkInDisplay =
    (hasResHotel &&
      pickTrimmedString(resHotel.checkInDate, resHotel.checkin, resHotel.check_in)) ||
    pickTrimmedString(row.dep_date, row.checkin, row.check_in, row.checkIn) ||
    "";

  const checkOutDisplay =
    (hasResHotel &&
      pickTrimmedString(resHotel.checkOutDate, resHotel.checkout, resHotel.check_out)) ||
    pickTrimmedString(row.ret_date, row.checkout, row.check_out, row.checkOut) ||
    "";

  let nights =
    (hasResHotel && typeof resHotel.nights === "number" && resHotel.nights > 0
      ? resHotel.nights
      : null) ??
    (rowHotel && typeof rowHotel.nights === "number" && rowHotel.nights > 0 ? rowHotel.nights : null) ??
    (typeof row.nights === "number" && row.nights > 0 ? row.nights : null);

  if (nights == null && checkInDisplay && checkOutDisplay) {
    nights = computeNightsBetween(checkInDisplay, checkOutDisplay);
  }

  const boardRaw = pickBoardRaw(row, hasResHotel ? resHotel : null, rowHotel);
  const boardLabel = boardRaw != null ? getLdsBoardBasisLabelHebrew(boardRaw) : "";

  const { rooms: roomsCount, adults: adultsCount, children: childrenCount } = countRoomsAndPax(row);
  const passengersParts = [];
  if (roomsCount > 0) passengersParts.push(`${roomsCount} חדרים`);
  if (adultsCount > 0) passengersParts.push(`${adultsCount} מבוגרים`);
  if (childrenCount > 0) passengersParts.push(`${childrenCount} ילדים`);
  const passengersLine = passengersParts.join(" · ");

  const orderIdRaw = pickTrimmedString(
    row.dealid,
    row.dealId,
    row.orderid,
    row.orderId,
    row.id,
    row.lds_id,
    row.ldsId
  );
  const orderIdDisplay = orderIdRaw ? `מס׳ ${orderIdRaw}` : "";

  const supplierBits = [];
  const pnr = reservation?.supplierPNR ?? reservation?.bookingRef ?? row.supplierPNR;
  if (pickTrimmedString(pnr)) supplierBits.push(`PNR ${pickTrimmedString(pnr)}`);
  const flights = reservation?.flight ?? reservation?.flights;
  const leg0 = Array.isArray(flights) && flights[0]?.leg ? flights[0].leg : null;
  const supId = pickTrimmedString(leg0?.supplierId, leg0?.supplierName, row.supplier_id, row.supplierId);
  if (supId) supplierBits.push(supId);
  const supplierLine = supplierBits.join(" · ");

  const et = pickTrimmedString(row.entity_type, row.entityType);
  const entityTypeLabel =
    et.toLowerCase() === "proposal" ? "הצעה" : et ? et : "";

  /** Image URL priority: row direct URLs → reservation.hotel → row.hotel → snapshots → deal → package */
  const imageCandidates = [];
  for (const u of rowLevelDirectImageUrls(row)) imageCandidates.push(u);
  if (hasResHotel) {
    for (const u of collectHotelLikeImageUrls(resHotel)) imageCandidates.push(u);
  }
  if (rowHotel) {
    for (const u of collectHotelLikeImageUrls(rowHotel)) imageCandidates.push(u);
  }
  for (const h of collectDealSnapshotHotels(row)) {
    for (const u of collectHotelLikeImageUrls(h)) imageCandidates.push(u);
  }
  const dealH = dealBlock?.hotel;
  if (nonEmptyObject(dealH)) {
    for (const u of collectHotelLikeImageUrls(dealH)) imageCandidates.push(u);
  }
  if (pkgHotel) {
    for (const u of collectHotelLikeImageUrls(pkgHotel)) imageCandidates.push(u);
  }
  if (hasSrHotel) {
    for (const u of collectHotelLikeImageUrls(searchResultHotel)) imageCandidates.push(u);
  }

  const prefetchedImageUrl = imageCandidates[0] || null;

  const hotelIdFromRes = hasResHotel
    ? resHotel.hotelId ?? resHotel.hotelid ?? resHotel.id ?? resHotel.HOTEL_ID
    : null;
  const hotelIdFromRowHotel = rowHotel
    ? rowHotel.hotelId ?? rowHotel.hotelid ?? rowHotel.id
    : null;
  const hotelIdFromSearch = hasSrHotel
    ? searchResultHotel.hotelId ?? searchResultHotel.hotelid ?? searchResultHotel.id
    : null;

  const nestedHotelObjectsForId = [...collectDealSnapshotHotels(row)];
  if (nonEmptyObject(dealH)) nestedHotelObjectsForId.push(dealH);
  if (pkgHotel) nestedHotelObjectsForId.push(pkgHotel);
  const hotelIdFromNestedHotels = firstNumericHotelIdFromHotelObjects(nestedHotelObjectsForId);

  const hotelId =
    hotelIdFromRes ??
    hotelIdFromRowHotel ??
    hotelIdFromSearch ??
    hotelIdFromNestedHotels ??
    row.hotelid ??
    row.hotelId ??
    null;

  return {
    hotelName,
    displayTitle,
    hotelSubtitle,
    checkInDisplay,
    checkOutDisplay,
    hotelId,
    prefetchedImageUrl,
    nights,
    boardLabel,
    roomsCount,
    adultsCount,
    childrenCount,
    passengersLine,
    orderIdDisplay,
    orderIdRaw,
    supplierLine,
    kindLabel: "חבילה",
    entityTypeLabel,
  };
}
